// Location: src/shared/components/AdvancedExpressionBuilder/types.ts

import { RESOURCE } from '../../../services/Hub/Authorization/authorization.constants';

// The main props for the component - public contract
export interface AdvancedExpressionBuilderProps {
  // Optional: The initial state of the expressions to render.
  initialExpressions?: ExpressionGroupsState;

  // Callback: Called whenever a change in the expressions results in a valid, completed state.
  onChange: (expressions: ExpressionGroupsState) => void;

  // Function Prop: The component will call this function to get the list of available fields.
  // This externalizes data fetching, making the component reusable for any data source.
  fetchFieldCatalog: (searchTerm?: string) => Promise<FieldDefinition[]>;

  // Function Prop: The component will call this function to get values for 'select' or 'multiselect' fields.
  // The `resourceId` is provided by the selected FieldDefinition.
  fetchResourceValues: (resourceId: string, searchTerm?: string) => Promise<SelectOption[]>;

  // Flag to display a loading state, controlled by the parent component.
  isLoading?: boolean;

  // Optional: An object to customize UI text, ensuring internationalization.
  locale?: { [key: string]: string };
}

export interface SelectOptionBO {
  label: string;
  value: string;
  group?: string;
}

export interface DynamicOptions {
  resource: RESOURCE;
  mapValue: string;
  mapLabel: string;
  groupBy?: string;
  orderBy?: string;
}

export type FieldDefinitionTypes = 'text' | 'numeric' | 'date' | 'boolean' | 'select' | 'multiselect' | 'currency';

export interface FieldDefinition {
  identifier: string;
  label: string;
  fieldType: FieldDefinitionTypes;
  components?: {
    nodes?: SelectOptionBO[];
    dynamicOptions?: DynamicOptions;
  };
  resolveToResourceId?: CONTEXT_FIELD_RESOLVE_TO_RESOURCE;
  dynamicOptions?: DynamicOptions;
}

export enum CONTEXT_FIELD_RESOLVE_TO_RESOURCE {
  USER = 'USER',
  TICKET = 'TICKET',
  CUSTOMER = 'CUSTOMER',
  WORKFLOW = 'WORKFLOW',
  TASK = 'TASK',
  TASK_TEMPLATE = 'TASK_TEMPLATE',
  PHASE = 'PHASE',
  DOCUMENT_FORMALIZATION_TICKET_TYPE = 'DOCUMENT_FORMALIZATION_TICKET_TYPE',
  ORGANIZATION_BRANCH = 'ORGANIZATION_BRANCH',
  SEASON_INTERVAL = 'SEASON_INTERVAL',
  TICKET_TYPE = 'TICKET_TYPE'
}

// Available operators for fields
export type Operator =
  | 'equals'
  | 'not_equals'
  | 'greater_than'
  | 'less_than'
  | 'present'
  | 'not_present'
  | 'includes'
  | 'not_includes'
  | 'between';

// Option for select/multiselect fields
export interface SelectOption {
  label: string;
  value: string;
}

// Internal state managed by the reducer - normalized structure
export interface ExpressionGroupsState {
  groups: { [groupId: string]: ExpressionGroup };
  operator: 'AND' | 'OR'; // The operator between the groups (OR)
}

export interface ExpressionGroup {
  expressions: { [expressionId: string]: Expression };
  operator: 'AND' | 'OR'; // The operator between expressions within a group (AND)
}

export interface Expression {
  field?: FieldDefinition;
  operator?: Operator;
  value: any[];
  isCompleted: boolean;
}

// Reducer action types
export type ExpressionAction =
  | { type: 'CREATE_GROUP'; payload: { groupId: string } }
  | { type: 'REMOVE_GROUP'; payload: { groupId: string } }
  | { type: 'CREATE_EXPRESSION'; payload: { groupId: string; expressionId: string } }
  | { type: 'REMOVE_EXPRESSION'; payload: { groupId: string; expressionId: string } }
  | { type: 'UPDATE_EXPRESSION'; payload: { groupId: string; expressionId: string; expression: Partial<Expression> } }
  | { type: 'SWITCH_GROUPS_OPERATOR'; payload: { operator: 'AND' | 'OR' } }
  | { type: 'SWITCH_EXPRESSIONS_OPERATOR'; payload: { groupId: string; operator: 'AND' | 'OR' } }
  | { type: 'SET_INITIAL_STATE'; payload: { state: ExpressionGroupsState } }
  | { type: 'RESET_STATE' };

// Default locale strings
export const defaultLocale = {
  addAndConditionButton: 'Add AND Condition',
  addOrConditionButton: 'Add OR Condition',
  addOrGroupButton: 'Add OR Group',
  removeButton: 'Remove',
  fieldPlaceholder: 'Select a field...',
  operatorPlaceholder: 'Select an operator...',
  valuePlaceholder: 'Enter value...',
  andOperator: 'AND',
  orOperator: 'OR',
  betweenFromPlaceholder: 'From',
  betweenToPlaceholder: 'To',
  trueBooleanLabel: 'True',
  falseBooleanLabel: 'False',
  searchPlaceholder: 'Search...',
} as const;

// API transformation interfaces - what gets sent to the backend
export interface ApiConditions {
  expressions: any[];
  expressionsStructure: ExpressionGroupsState;
}